<template>
  <div class="anchor-home">
    <div class="container">
      <!-- 主播顶卡 -->
      <div class="anchor-banner" ref="bannerEl">
        <img :src="bannerUrl" alt="主播主页封面" class="banner-bg" @error="handleImageError">
        <div class="banner-overlay"></div>
        <div class="anchor-top">
          <div class="anchor-avatar-wrap">
            <img :src="avatarUrl" class="anchor-avatar" @error="handleAvatarError" alt="">
            <span class="anchor-level-badge" v-if="anchorInfo.level && anchorInfo.level > 1">LV{{ anchorInfo.level }}</span>
          </div>
          <div class="anchor-meta">
            <div class="anchor-name-row">
              <span class="anchor-nickname">{{ anchorInfo.nickname || '神秘主播' }}</span>
              <svg v-if="anchorInfo.verify" viewBox="0 0 24 24" width="20" height="20" fill="#1890ff" class="verify-icon"><circle cx="11" cy="11" r="8"/><path d="M9 11l2 2 4-4"/></svg>
            </div>
            <div class="anchor-id">靓号：{{ anchorInfo.id || '-' }}</div>
            <div class="anchor-stats">
              <span class="stat-item">粉丝 {{ anchorInfo.fans_count || 0 }}</span>
              <span class="stat-item">总观看 {{ anchorInfo.total_views || 0 }}</span>
              <span class="stat-item">等级 LV{{ anchorInfo.level || 1 }}</span>
            </div>
          </div>
          <div class="anchor-actions">
            <button
              v-if="isLoggedIn && !isOwnProfile"
              type="button"
              class="btn-msg"
              @click="goPrivateMessage(anchorInfo)"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M21 15a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              发私信
            </button>
            <button
              v-if="!isOwnProfile"
              type="button"
              class="btn-follow"
              :class="{ followed: isFollowing }"
              :disabled="followSubmitting"
              @click="toggleFollow"
            >
              <svg v-if="!isFollowing" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.27 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.77-3.4 6.86-8.45 11.54L12 21.35z"/></svg>
              {{ followSubmitting ? '处理中' : (isFollowing ? '已关注' : '关注') }}
            </button>
          </div>
        </div>
      </div>

      <!-- 标签页导航 -->
      <div class="anchor-tabs-wrap">
        <div class="anchor-tabs">
          <span class="tab-item" :class="{ active: activeTab === 'room' }" @click="activeTab = 'room'">直播间</span>
          <span class="tab-item" :class="{ active: activeTab === 'posts' }" @click="activeTab = 'posts'">动态</span>
          <span class="tab-item" :class="{ active: activeTab === 'work' }" @click="activeTab = 'work'">作品</span>
          <span class="tab-item" :class="{ active: activeTab === 'stats' }" @click="activeTab = 'stats'">数据</span>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="anchor-content">
        <div v-if="loading" class="page-state">正在加载主播资料...</div>
        <div v-else-if="loadError" class="page-state error">{{ loadError }}</div>

        <template v-else-if="activeTab === 'room'">
          <div class="room-section">
            <div v-if="liveRoom" class="live-preview" @click="goFeaturedRoom">
              <img :src="getImageUrl(liveRoom.cover)" alt="直播间封面" class="live-cover" @error="handleImageError">
              <span class="live-tag" :class="{ replay: !liveRoom.is_live }">{{ liveRoom.is_live ? '直播中' : '最新作品' }}</span>
              <span class="live-viewers">{{ liveRoom.viewer_count || 0 }} 人观看</span>
            </div>
            <div v-else class="no-live">
              <p>主播暂时没有直播或作品</p>
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === 'posts'">
          <div class="posts-section">
            <div v-if="postList.length === 0" class="empty-text">暂无动态</div>
            <div v-else class="post-list">
              <div class="post-item" v-for="post in postList" :key="post.id">
                <div class="post-content">{{ post.content }}</div>
                <div class="post-time">{{ formatTime(post.createtime) }}</div>

                <!-- 点赞 / 评论 -->
                <div class="post-actions">
                  <button class="post-action-btn" :class="{ liked: post.is_liked }" @click="togglePostLike(post)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" :fill="post.is_liked ? '#ff4757' : 'none'" :stroke="post.is_liked ? '#ff4757' : '#999'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                    <span>{{ post.like_count || 0 }}</span>
                  </button>
                  <button class="post-action-btn" :class="{ active: expandedPostComments[post.id] }" @click="togglePostComments(post)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span>{{ post.comment_count || 0 }}</span>
                  </button>
                </div>

                <!-- 评论展开区 -->
                <div v-if="expandedPostComments[post.id]" class="post-comments">
                  <div v-if="postCommentLoading[post.id]" class="post-comment-tip">加载评论中...</div>
                  <template v-else>
                    <div v-if="!postCommentsMap[post.id] || postCommentsMap[post.id].length === 0" class="post-comment-tip">暂无评论，快来抢沙发吧~</div>
                    <div v-else class="post-comment-list">
                      <div class="post-comment-item" v-for="c in postCommentsMap[post.id]" :key="c.id">
                        <img :src="c.avatar || defaultAvatar" alt="头像" class="post-comment-avatar">
                        <div class="post-comment-body">
                          <div class="post-comment-author">{{ c.nickname || c.username }}</div>
                          <div class="post-comment-text">{{ c.content }}</div>
                          <div class="post-comment-time">{{ formatTime(c.createtime) }}</div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <div class="post-comment-input">
                    <input
                      type="text"
                      v-model="postCommentInput[post.id]"
                      placeholder="发表评论..."
                      @keyup.enter="submitPostComment(post)"
                    >
                    <button
                      :disabled="postCommentSubmitting[post.id]"
                      @click="submitPostComment(post)"
                    >{{ postCommentSubmitting[post.id] ? '发送中' : '发送' }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === 'work'">
          <div class="work-section">
            <div v-if="workList.length === 0" class="empty-text">暂无作品</div>
            <div v-else class="work-grid">
              <div class="work-item" v-for="work in workList" :key="work.id" @click="goLiveDetail(work.id)">
                <img :src="getImageUrl(work.cover)" :alt="work.title" @error="handleImageError">
                <div class="work-info">
                  <div class="work-title">{{ work.title }}</div>
                  <div class="work-stats">{{ work.views || 0 }}观看</div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="stats-section">
            <div class="stat-cards">
              <div class="stat-card">
                <div class="stat-value">{{ liveCount }}</div>
                <div class="stat-label">作品数量</div>
              </div>
              <div class="stat-card">
                <div class="stat-value">{{ totalFans }}</div>
                <div class="stat-label">总粉丝</div>
              </div>
              <div class="stat-card">
                <div class="stat-value">{{ formatThousands(totalViews) }}</div>
                <div class="stat-label">总观看</div>
              </div>
            </div>
            <div class="level-detail">
              <h4>等级特权</h4>
              <div class="level-privileges">
                <div class="lp-item" v-for="(p, i) in levelPrivileges" :key="p.id || p.name || i">
                  <svg class="lp-icon" v-html="p.icon" viewBox="0 0 24 24" width="20" height="20"></svg>
                  <div class="lp-text">{{ p.text }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '../stores/user';
import { BASE_URL } from '../api/config';
import { getAnchorPublicProfile } from '../api/anchor';
import { follow, unfollow } from '../api/follow';
import { likePost, unlikePost, getPostComments, addPostComment } from '../api/post';
import { avatarFallback } from '../utils/avatar';

const EMPTY_ANCHOR_INFO = {
  id: 0,
  nickname: '神秘主播',
  username: '',
  avatar: '',
  bio: '',
  level: 1,
  fans_count: 0,
  total_views: 0,
  video_count: 0,
  verify: 0
};

const TEST_LEVEL_PRIVILEGES = [
  { id: 1, name: 'high-quality', text: '高品质直播', icon: '<path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7.91 15.14 4 9.27z"/>' },
  { id: 2, name: 'special-gift', text: '尊享专属', icon: '<path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8 2.4-7.2-6-4.8h7.6z"/>' },
  { id: 3, name: 'no-color-restriction', text: '彩色弹幕', icon: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>' },
  { id: 4, name: 'priority-entry', text: '优先入场', icon: '<path d="M5 12h14M12 5l7 7-7 7"/>' }
];

export default {
  name: 'AnchorHome',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const userStore = useUserStore();
    const anchorId = ref(parseInt(route.params.id, 10) || 0);
    const anchorInfo = ref({ ...EMPTY_ANCHOR_INFO });
    const liveRoom = ref(null);
    const postList = ref([]);
    const workList = ref([]);
    const isFollowing = ref(false);
    const followSubmitting = ref(false);
    const loading = ref(true);
    const loadError = ref('');
    const activeTab = ref('room');
    const defaultAvatar = require('@/assets/logo.png');
    const defaultBanner = require('@/assets/1.jpg');
    let loadSequence = 0;

    // 动态互动：点赞 / 评论
    const expandedPostComments = ref({});
    const postCommentsMap = ref({});
    const postCommentInput = ref({});
    const postCommentLoading = ref({});
    const postCommentSubmitting = ref({});

    const requireLogin = () => {
      if (isLoggedIn.value) return true;
      router.push({ path: '/login', query: { redirect: route.fullPath } });
      return false;
    };

    const togglePostLike = async (post) => {
      if (!requireLogin()) return;
      const apiFn = post.is_liked ? unlikePost : likePost;
      try {
        const res = await apiFn(post.id);
        if (res.code === 1) {
          post.is_liked = Boolean(res.data && res.data.is_liked);
          post.like_count = Number(res.data && res.data.like_count) || 0;
        } else {
          alert(res.msg || '操作失败');
        }
      } catch (e) {
        alert(e.response?.data?.msg || e.message || '操作失败');
      }
    };

    const togglePostComments = async (post) => {
      if (expandedPostComments.value[post.id]) {
        expandedPostComments.value[post.id] = false;
        return;
      }
      expandedPostComments.value[post.id] = true;
      if (!postCommentsMap.value[post.id] || postCommentsMap.value[post.id].length === 0) {
        await loadPostComments(post);
      }
    };

    const loadPostComments = async (post) => {
      postCommentLoading.value[post.id] = true;
      try {
        const res = await getPostComments({ post_id: post.id, limit: 50 });
        if (res.code === 1) {
          const items = res.data?.list || res.data?.data || [];
          postCommentsMap.value[post.id] = items;
        }
      } catch (e) {
        console.error('加载评论失败', e);
      } finally {
        postCommentLoading.value[post.id] = false;
      }
    };

    const submitPostComment = async (post) => {
      if (!requireLogin()) return;
      const content = (postCommentInput.value[post.id] || '').trim();
      if (!content) {
        alert('请输入评论内容');
        return;
      }
      postCommentSubmitting.value[post.id] = true;
      try {
        const res = await addPostComment({ post_id: post.id, content });
        if (res.code === 1) {
          postCommentInput.value[post.id] = '';
          post.comment_count = (post.comment_count || 0) + 1;
          await loadPostComments(post);
        } else {
          alert(res.msg || '评论失败');
        }
      } catch (e) {
        alert(e.response?.data?.msg || e.message || '评论失败');
      } finally {
        postCommentSubmitting.value[post.id] = false;
      }
    };

    const avatarUrl = computed(() => {
      const a = anchorInfo.value.avatar || '';
      if (!a) return avatarFallback(anchorInfo.value.nickname || '主播', anchorInfo.value.user_id || anchorInfo.value.id);
      if (a.indexOf('http') === 0 || a.indexOf('data:') === 0) return a;
      return BASE_URL + (a.indexOf('/') === 0 ? a : '/' + a);
    });

    const getImageUrl = (url) => {
      const source = String(url || '').trim();
      if (!source) return defaultBanner;
      if (/^(https?:|data:|blob:)/i.test(source)) return source;
      return BASE_URL + (source.startsWith('/') ? source : '/' + source);
    };

    const bannerUrl = computed(() => {
      return getImageUrl(liveRoom.value?.cover || workList.value[0]?.cover || anchorInfo.value.cover);
    });

    const isLoggedIn = computed(() => {
      return !!(userStore.token && !userStore.isTokenExpired());
    });

    const totalViews = computed(() => {
      return anchorInfo.value.total_views || 0;
    });

    const totalFans = computed(() => {
      return anchorInfo.value.fans_count || 0;
    });

    const liveCount = computed(() => {
      return Number(anchorInfo.value.video_count || workList.value.length || 0);
    });

    const isOwnProfile = computed(() => {
      return isLoggedIn.value
        && String(userStore.userInfo?.id || '') === String(anchorInfo.value.id || '');
    });

    const levelPrivileges = TEST_LEVEL_PRIVILEGES;

    const handleAvatarError = (e) => {
      e.target.src = defaultAvatar;
    };

    const handleImageError = (e) => {
      if (e.target.dataset.fallbackApplied === '1') return;
      e.target.dataset.fallbackApplied = '1';
      e.target.src = defaultBanner;
    };

    const goPrivateMessage = (anchor) => {
      if (!isLoggedIn.value) {
        router.push('/login');
        return;
      }
      if (userStore.userInfo && String(userStore.userInfo.id) === String(anchor.id)) {
        alert('不能给自己发送私信');
        return;
      }
      sessionStorage.setItem('msg_target_user', JSON.stringify({
        id: anchor.id,
        nickname: anchor.nickname,
        avatar: anchor.avatar
      }));
      router.push({ path: '/user', query: { tab: 'messages' } });
    };

    const toggleFollow = async () => {
      if (!isLoggedIn.value) {
        router.push({ path: '/login', query: { redirect: route.fullPath } });
        return;
      }
      if (isOwnProfile.value || followSubmitting.value) return;

      const wasFollowing = isFollowing.value;
      followSubmitting.value = true;
      try {
        const res = await (wasFollowing ? unfollow : follow)(anchorInfo.value.id);
        if (res.code !== 1) throw new Error(res.msg || '操作失败');

        isFollowing.value = !wasFollowing;
        const currentFans = Number(anchorInfo.value.fans_count || 0);
        anchorInfo.value.fans_count = Math.max(0, currentFans + (wasFollowing ? -1 : 1));
      } catch (e) {
        alert(e.response?.data?.msg || e.message || '关注操作失败，请稍后重试');
      } finally {
        followSubmitting.value = false;
      }
    };

    const goLiveDetail = (id) => {
      const videoId = Number(id);
      if (videoId > 0) router.push({ name: 'LiveDetail', params: { id: videoId } });
    };

    const goFeaturedRoom = () => {
      if (!liveRoom.value?.id) return;
      if (liveRoom.value.is_live) {
        router.push({ name: 'LiveRoomDetail', params: { id: liveRoom.value.id } });
      } else {
        goLiveDetail(liveRoom.value.id);
      }
    };

    const formatTime = (ts) => {
      if (!ts) return '';
      let d;
      if (typeof ts === 'string' && !isNaN(Date.parse(ts))) {
        d = new Date(ts);
      } else {
        const num = Number(ts);
        if (num <= 0) return '';
        d = new Date(num * 1000);
      }
      if (isNaN(d.getTime())) return '';
      const pad = (n) => (n < 10 ? '0' + n : '' + n);
      const now = new Date();
      const diff = Math.floor((now - d) / 1000);
      if (diff < 60) return '刚刚';
      if (diff < 3600) return Math.floor(diff / 60) + '分钟前';
      if (diff < 86400) return Math.floor(diff / 3600) + '小时前';
      if (diff < 259200) return Math.floor(diff / 86400) + '天前';
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const formatThousands = (value) => {
      const num = Number(value) || 0;
      return num >= 10000 ? (num / 10000).toFixed(1) + '万' : String(num);
    };

    const loadAnchorPage = async (id) => {
      const normalizedId = parseInt(id, 10) || 0;
      const requestId = ++loadSequence;

      anchorId.value = normalizedId;
      anchorInfo.value = { ...EMPTY_ANCHOR_INFO, id: normalizedId };
      liveRoom.value = null;
      postList.value = [];
      workList.value = [];
      isFollowing.value = false;
      activeTab.value = 'room';
      loading.value = true;
      loadError.value = '';

      if (!normalizedId) {
        loadError.value = '主播编号无效';
        loading.value = false;
        return;
      }

      try {
        const res = await getAnchorPublicProfile(normalizedId);
        if (requestId !== loadSequence) return;
        if (res.code !== 1 || !res.data?.user) {
          throw new Error(res.msg || '主播资料加载失败');
        }

        const data = res.data;
        anchorInfo.value = {
          ...EMPTY_ANCHOR_INFO,
          ...data.user,
          ...(data.statistics || {}),
          id: normalizedId
        };
        workList.value = Array.isArray(data.works) ? data.works : [];
        postList.value = Array.isArray(data.posts) ? data.posts : [];

        const featuredVideo = data.featured_video || null;
        if (data.live_room) {
          liveRoom.value = {
            ...data.live_room,
            cover: data.live_room.cover || featuredVideo?.cover || '',
            viewer_count: Number(data.live_room.viewer_count ?? data.live_room.view_count ?? 0),
            is_live: true
          };
        } else if (featuredVideo) {
          liveRoom.value = {
            ...featuredVideo,
            viewer_count: Number(featuredVideo.views || 0),
            is_live: false
          };
        }
        isFollowing.value = Boolean(data.is_following);
      } catch (e) {
        if (requestId !== loadSequence) return;
        loadError.value = e.response?.data?.msg || e.message || '主播资料加载失败';
      } finally {
        if (requestId === loadSequence) {
          loading.value = false;
          // 支持 ?tab=posts 直达动态页
          if (route.query.tab === 'posts') {
            activeTab.value = 'posts';
          }
        }
      }
    };

    onMounted(() => loadAnchorPage(route.params.id));
    watch(() => route.params.id, (nextId, previousId) => {
      if (nextId !== previousId) loadAnchorPage(nextId);
    });
    watch(() => route.query.tab, (tab) => {
      if (tab === 'posts' && postList.value.length > 0) {
        activeTab.value = 'posts';
      }
    });

    return {
      route,
      anchorId,
      anchorInfo,
      liveRoom,
      postList,
      workList,
      isFollowing,
      followSubmitting,
      loading,
      loadError,
      activeTab,
      defaultAvatar,
      defaultBanner,
      getImageUrl,
      avatarUrl,
      bannerUrl,
      isLoggedIn,
      isOwnProfile,
      liveCount,
      totalFans,
      totalViews,
      levelPrivileges,
      handleAvatarError,
      handleImageError,
      goPrivateMessage,
      goFeaturedRoom,
      goLiveDetail,
      toggleFollow,
      formatTime,
      formatThousands,
      // 动态互动
      expandedPostComments,
      postCommentsMap,
      postCommentInput,
      postCommentLoading,
      postCommentSubmitting,
      togglePostLike,
      togglePostComments,
      loadPostComments,
      submitPostComment
    };
  }
};
</script>

<style scoped>
.anchor-home {
  min-height: calc(100vh - 56px);
  background: #f7f8fa;
  color: #333;
}
.anchor-home .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

/* ============== Banner ============== */
.anchor-banner {
  position: relative;
  height: 200px;
  border-radius: 0 0 12px 12px;
  overflow: visible;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
.banner-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.85);
  border-radius: 0 0 12px 12px;
}
.banner-overlay {
  position: absolute;
  inset: 0;
  border-radius: 0 0 12px 12px;
  background: linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 60%);
}

/* 顶卡左下：头像 + 名称 + 操作 */
.anchor-top {
  position: absolute;
  left: 40px;
  right: 40px;
  bottom: 16px;
  display: flex;
  align-items: flex-end;
  gap: 16px;
  z-index: 2;
}
.anchor-avatar-wrap {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  border: 4px solid #fff;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  overflow: hidden;
  background: #fff;
}
.anchor-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.anchor-level-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  line-height: 1.6;
  transform: translate(10%, 10%);
}
.anchor-meta {
  display: flex;
  flex-direction: column;
  color: #fff;
  padding-bottom: 8px;
  flex: 1;
  min-width: 0;
}
.anchor-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 20px;
  font-weight: 700;
}
.anchor-nickname {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.verify-icon {
  width: 20px;
  height: 20px;
}
.anchor-id {
  font-size: 13px;
  color: rgba(255,255,255,0.85);
  margin-top: 2px;
}
.anchor-stats {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: rgba(255,255,255,0.85);
  margin-top: 6px;
}
.stat-item::before {
  content: '・';
  margin-right: 6px;
}
.anchor-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  flex-shrink: 0;
}
.btn-msg,
.btn-follow {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  outline: none;
}
.btn-msg {
  background: #fff;
  color: #ff6a00;
}
.btn-msg:hover {
  transform: scale(1.05);
}
.btn-follow {
  background: rgba(255,255,255,0.2);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.4);
}
.btn-follow.followed {
  background: rgba(255,150,26,0.9);
  color: #fff;
  border-color: transparent;
}
.btn-follow:hover {
  filter: brightness(1.1);
}
.btn-follow:disabled {
  cursor: wait;
  opacity: 0.7;
}
.btn-msg:focus-visible,
.btn-follow:focus-visible,
.tab-item:focus-visible {
  outline: 2px solid #ff8a1f;
  outline-offset: 2px;
}

/* ============== 标签页 ============== */
.anchor-tabs-wrap {
  margin-top: 20px;
}
.anchor-tabs {
  display: flex;
  gap: 24px;
  border-bottom: 2px solid #e8e8e8;
  font-size: 15px;
  font-weight: 600;
}
.tab-item {
  padding: 10px 4px;
  cursor: pointer;
  color: #999;
  transition: all 0.2s;
  position: relative;
  bottom: -2px;
}
.tab-item.active {
  color: #ff6a00;
  border-bottom: 3px solid #ff6a00;
}

/* ============== 内容区 ============== */
.anchor-content {
  min-height: 300px;
  padding: 24px 0;
}
.page-state {
  padding: 56px 20px;
  text-align: center;
  color: #777;
}
.page-state.error {
  color: #d4380d;
}
.room-section,
.posts-section,
.work-section,
.stats-section {
  min-height: 200px;
}
.live-preview {
  position: relative;
  width: 320px;
  max-width: 100%;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.live-preview:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.14);
}
.live-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.live-tag {
  position: absolute;
  top: 8px;
  left: 8px;
  background: #ff4d6d;
  color: #fff;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
}
.live-tag.replay {
  background: rgba(35, 43, 59, 0.82);
}
.live-viewers {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0,0,0,0.5);
  color: #fff;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
}
.no-live,
.empty-text {
  padding: 40px 0;
  text-align: center;
  color: #999;
}
.post-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.post-item {
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.post-content {
  font-size: 14px;
  color: #333;
  margin-bottom: 6px;
}
.post-time {
  font-size: 11px;
  color: #bbb;
}
/* 动态点赞/评论 */
.post-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.post-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  font-size: 12px;
  color: #999;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  transition: all 0.2s;
}
.post-action-btn:hover {
  background: #f5f5f5;
}
.post-action-btn.liked {
  color: #ff4757;
}
.post-action-btn.active {
  color: #ff9600;
  background: #fff5eb;
}
/* 评论展开区 */
.post-comments {
  margin-top: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}
.post-comment-tip {
  text-align: center;
  color: #bbb;
  font-size: 12px;
  padding: 12px 0;
}
.post-comment-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.post-comment-item {
  display: flex;
  gap: 8px;
}
.post-comment-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.post-comment-body {
  flex: 1;
  min-width: 0;
}
.post-comment-author {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}
.post-comment-text {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  word-break: break-word;
}
.post-comment-time {
  font-size: 10px;
  color: #bbb;
  margin-top: 2px;
}
.post-comment-input {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}
.post-comment-input input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-size: 12px;
  outline: none;
  background: #fff;
  box-sizing: border-box;
}
.post-comment-input input:focus {
  border-color: #ff9600;
}
.post-comment-input button {
  padding: 0 16px;
  border: none;
  border-radius: 16px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.post-comment-input button:disabled {
  background: #ccc;
  cursor: not-allowed;
}
.work-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.work-item {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.work-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
}
.work-item img {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}
.work-info {
  padding: 10px;
}
.work-title {
  font-size: 13px;
  color: #333;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.work-stats {
  font-size: 11px;
  color: #999;
}
.stat-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}
.stat-card {
  flex: 1;
  background: #fff;
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #ff6a00;
  margin-bottom: 6px;
}
.stat-label {
  font-size: 13px;
  color: #999;
}
.level-detail h4 {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}
.level-privileges {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}
.lp-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.lp-item svg {
  width: 24px;
  height: 24px;
  color: #ff9600;
}
.lp-text {
  font-size: 13px;
  color: #666;
}

/* ============== 响应式 ============== */
@media (max-width: 768px) {
  .anchor-banner {
    height: 160px;
  }
  .anchor-top {
    left: 16px;
    right: 16px;
    bottom: 12px;
    gap: 10px;
  }
  .anchor-avatar-wrap {
    width: 72px;
    height: 72px;
    border-width: 3px;
  }
  .anchor-meta {
    display: none;
  }
  .anchor-actions {
    margin-left: auto;
    padding-bottom: 4px;
  }
  .btn-msg,
  .btn-follow {
    padding: 6px 12px;
    font-size: 12px;
  }
  .anchor-tabs-wrap {
    margin-top: 16px;
  }
  .work-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .stat-cards {
    flex-wrap: wrap;
  }
  .stat-card {
    flex: 1 1 calc(50% - 10px);
  }
}
</style>
